/**
 * useQuery hook for a publication's section/folder hierarchy.
 *
 * Replaces `publicationViewEffects.getPublicationFoldersHierarchy` (compose-app).
 * Mirrors the same two-step Hasura merge via `postQuery`:
 *   1. `getPublicationRootFolder` → root folder id
 *   2. `getFoldersOfPublication` (filtered by sectionIds) → full folder set
 * then builds the hierarchy map and groups it, exactly as the retired effect did.
 *
 * Auth note (§1): non-admin reachable → `postQuery`, not `useGraphQLQuery`.
 *
 * `sectionIds` is the access-control filter the retired effect read from
 * `subjectClaimsState` (sectionIds: 'All' or a list). It is passed in as a
 * param — the caller sources it from the subjectClaims bridge until that slice
 * is retired. It is part of the query key so a claims change refetches.
 *
 * Pass `null` to disable during route transitions before `entityId > 0` (§9).
 */

import { useQuery } from '@tanstack/react-query';
import { getFoldersOfPublication, getPublicationRootFolder } from '@opengov/stories-shared/graphQL';
import { buildPublicationFolderHierarchyMap } from '@opengov/stories-shared/utils';

import { postQuery } from '../../../../effects/publications/postQuery';
import { groupAllStoryFolders } from '../../../../utils';
import { useToast } from '../../../Toast/context/useToast.context';
import { IStoryFolder } from '../../../../../types';
import { publicationViewQueryKeys, FoldersHierarchyKeyParams } from './publicationViewQueryKeys';

export type UsePublicationFoldersHierarchyParams = FoldersHierarchyKeyParams | null;

export type PublicationFoldersHierarchy = {
  publicationRootFolderId: string;
  publicationFolders: IStoryFolder[];
};

export function usePublicationFoldersHierarchy(
  params: UsePublicationFoldersHierarchyParams,
  errorMessage: string
) {
  const { toggleToast } = useToast();

  return useQuery<PublicationFoldersHierarchy, Error>({
    queryKey: params
      ? publicationViewQueryKeys.foldersHierarchy(params)
      : ([...publicationViewQueryKeys.all, 'foldersHierarchy', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const { publicationId, entityId, sectionIds } = params;

      const rootResponse = await postQuery(getPublicationRootFolder, {
        _publicationId: publicationId,
        _entityId: entityId
      });
      if (!rootResponse.publicationFolders?.length) {
        return Promise.reject(rootResponse.errors);
      }
      const publicationRootFolderId = rootResponse.publicationFolders[0].folderId;

      const where = {
        publicationId: { _eq: publicationId },
        deletedAt: { _is_null: true },
        ...(sectionIds !== 'All' && { id: { _in: sectionIds } })
      };

      const allFolders = await postQuery(getFoldersOfPublication, { where });
      const publicationFolderMap = buildPublicationFolderHierarchyMap(allFolders.storyFolders);

      const publicationFolders =
        sectionIds === 'All'
          ? publicationFolderMap[publicationRootFolderId].subFolders.map((subFolder) => ({
              storyFolder: subFolder
            }))
          : Object.values(publicationFolderMap).map((subfolder) => ({
              storyFolder: { ...subfolder, subFolders: [] }
            }));

      return {
        publicationRootFolderId,
        publicationFolders: groupAllStoryFolders(publicationFolders)
      };
    },
    enabled: params !== null,
    onError: () => {
      toggleToast({ title: errorMessage, severity: 'error' });
    }
  });
}
